import { FC, useState, useEffect } from 'react';
import { Add, Close, Save } from '@mui/icons-material';
import { Grid, MenuItem, TextField, Box, ButtonGroup, Button, Divider } from '@mui/material';
import { Form, Formik } from 'formik';
import { useHistory, useParams, Prompt } from 'react-router-dom';
import { deepEqual } from 'fast-equals';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { logError } from '../../../../services';
import {
  getColorSchemeOptionUris,
  putColorSchemeGeneralInfo,
  getColorSchemeOption,
  getColorSchemeAreas,
  createColorScheme
} from '../../../../fetch';
import {
  IColorSchemeGeneralInfo,
  IColorSchemeGeneralInfoPut,
  IDropdownResponse
} from '../../../../models';
import { Loader, SMBSelect } from '../../../../components';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';

interface IGeneralInformation {
  generalInfo: IColorSchemeGeneralInfo;
  isLoadingGeneralInfo: boolean;
  setDirtyForm: (val: boolean) => void;
  refetchGeneralInfo: () => void;
}

const GeneralInfoSchema = Yup.object().shape({
  isActive: Yup.string().required(),
  schemeArea: Yup.string().required(),
  upgradeFee: Yup.number()
    .typeError('This value must be a number.')
    .min(0, 'Must be greater than or equal to 0')
    .max(10000000, 'Must be at most 10000000'),
  brickMortarColor: Yup.string().required(),
  roofColor: Yup.string().required(),
  trimColor: Yup.string().required(),
  paintColor: Yup.string().required(),
  shutterPaintColor: Yup.string().required(),
  windowColor: Yup.string().required(),
  garageDoorColor: Yup.string().required(),
  brickMortarColorUri: Yup.array().required()
});

export const GeneralInformation: FC<IGeneralInformation> = ({
  generalInfo,
  isLoadingGeneralInfo,
  setDirtyForm,
  refetchGeneralInfo
}) => {
  const history = useHistory();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { colorSchemeId }: { colorSchemeId: string } = useParams();
  const [colorSchemeAreasAdd, setColorSchemeAreasAdd] = useState<any>();
  const [isLoadingOptions, setLoadingOptions] = useState<boolean>(false);
  const [brickMortarOptionsAdd, setBrickMortarOptionsAdd] = useState<IDropdownResponse[]>();
  const [roofOptionsAdd, setRoofOptionsAdd] = useState<IDropdownResponse[]>();
  const [trimOptionsAdd, setTrimOptionsAdd] = useState<IDropdownResponse[]>();
  const [paintOptionsAdd, setPaintOptionsAdd] = useState<IDropdownResponse[]>();
  const [shutterPaintOptionsAdd, setShutterPaintOptionsAdd] = useState<IDropdownResponse[]>();
  const [windowOptionsAdd, setWindowOptionsAdd] = useState<IDropdownResponse[]>();
  const [garageDoorOptionsAdd, setGarageDoorOptionsAdd] = useState<IDropdownResponse[]>();

  const fetchOptionsForAdd = async () => {
    setLoadingOptions(true);
    try {
      const areas = await getColorSchemeAreas();
      setColorSchemeAreasAdd(areas);
      const brickMortar = await getColorSchemeOption('BrickMortarColor');
      setBrickMortarOptionsAdd(brickMortar);
      const roof = await getColorSchemeOption('RoofColor');
      setRoofOptionsAdd(roof);
      const trim = await getColorSchemeOption('TrimColor');
      setTrimOptionsAdd(trim);
      const paint = await getColorSchemeOption('PaintColor');
      setPaintOptionsAdd(paint);
      const shutterPaint = await getColorSchemeOption('ShutterPaintColor');
      setShutterPaintOptionsAdd(shutterPaint);
      const window = await getColorSchemeOption('WindowColor');
      setWindowOptionsAdd(window);
      const garageDoor = await getColorSchemeOption('GarageDoorColor');
      setGarageDoorOptionsAdd(garageDoor);
    } catch (error) {
      logError(error, 'getColorSchemeOptionUris failed');
      return Promise.reject(error);
    } finally {
      setLoadingOptions(false);
    }
  };

  const fetchColorSchemeOptionUris = async (colorSchemeOption: string, optionValue: string) => {
    try {
      const res = await getColorSchemeOptionUris(colorSchemeOption, optionValue);
      return res;
    } catch (error) {
      logError(error, 'getColorSchemeOptionUris failed');
      return Promise.reject(error);
    }
  };

  useEffect(() => {
    if (colorSchemeId === 'add') {
      fetchOptionsForAdd();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ColorOptions = ({ options, name }) => {
    if (options?.length === 1) {
      return (
        <Box mt={0.5} mb={0.5}>
          {options.map((uri, index) => {
            return (
              <div style={{ overflow: 'hidden', height: 125, width: '100%' }}>
                <img
                  key={`${name + index}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                  src={uri}
                  alt={`${name + index}`}
                />
              </div>
            );
          })}
        </Box>
      );
    } else {
      return (
        <Box mt={0.5} mb={0.5}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              overflowX: 'auto'
            }}
          >
            {options.map((uri, index) => {
              return (
                <img
                  key={`${name + index}`}
                  style={{
                    width: `${100 / options.length}%`, // distribute width equally
                    height: 125,
                    maxWidth: '100%',
                    minWidth: '50px',
                    marginLeft: index > 0 ? '10px' : '0'
                  }}
                  src={uri}
                  alt={`${name + index}`}
                />
              );
            })}
          </div>
        </Box>
      );
    }
  };

  return (
    <>
      {(isLoadingGeneralInfo || isLoadingOptions) && (
        <Box mt={1} minHeight="30rem" display="flex" flexDirection="column" justifyContent="center">
          <Loader type="inline" position="centered">
            Loading...
          </Loader>
        </Box>
      )}
      {!isLoadingGeneralInfo && !isLoadingOptions && (
        <Formik
          enableReinitialize={true}
          initialValues={{
            ...generalInfo,
            isActive:
              generalInfo?.isActive === true ? 'Yes' : generalInfo?.isActive === false ? 'No' : '',
            schemeArea: generalInfo?.schemeArea ?? '',
            upgradeFee: generalInfo?.upgradeFee ?? 0,
            brickMortarColor: generalInfo?.brickMortarColor?.selectedValue?.value ?? '',
            roofColor: generalInfo?.roofColor?.selectedValue?.value ?? '',
            trimColor: generalInfo?.trimColor?.selectedValue?.value ?? '',
            paintColor: generalInfo?.paintColor?.selectedValue?.value ?? '',
            shutterPaintColor: generalInfo?.shutterPaintColor?.selectedValue?.value ?? '',
            windowColor: generalInfo?.windowColor?.selectedValue?.value ?? '',
            garageDoorColor: generalInfo?.garageDoorColor?.selectedValue?.value ?? '',
            brickMortarColorUri: generalInfo?.brickMortarColor?.selectedValueUri ?? [],
            roofColorUri: generalInfo?.roofColor?.selectedValueUri ?? [],
            trimColorUri: generalInfo?.trimColor?.selectedValueUri ?? [],
            paintColorUris: generalInfo?.paintColor?.selectedValueUri ?? [],
            shutterPaintColorUris: generalInfo?.shutterPaintColor?.selectedValueUri ?? [],
            windowColorUri: generalInfo?.windowColor?.selectedValueUri ?? [],
            garageDoorColorUri: generalInfo?.garageDoorColor?.selectedValueUri ?? []
          }}
          validationSchema={GeneralInfoSchema}
          onSubmit={async (values, actions) => {
            try {
              const data: IColorSchemeGeneralInfoPut = {
                isActive: values.isActive === 'Yes' ? true : false,
                schemeArea: values.schemeArea,
                upgradeFee: values.upgradeFee ?? 0,
                brickMortarColor: values.brickMortarColor,
                roofColor: values.roofColor,
                trimColor: values.trimColor,
                paintColor: values.paintColor,
                shutterPaintColor: values.shutterPaintColor,
                windowColor: values.windowColor,
                garageDoorColor: values.garageDoorColor
              };

              if (colorSchemeId !== 'add') {
                await putColorSchemeGeneralInfo(parseInt(colorSchemeId), data);
                refetchGeneralInfo();
                enqueueSnackbar('Color scheme has been updated successfully!', {
                  variant: 'success'
                });
              }

              if (colorSchemeId === 'add') {
                const colorSchemeId = await createColorScheme(data);
                actions.resetForm();
                enqueueSnackbar('Color Scheme has been created successfully!', {
                  variant: 'success'
                });

                history.push(`/development/color-schemes/${colorSchemeId}/general-information`);
              }
            } catch (error) {
              enqueueSnackbar(
                error?.Detail ?? 'Something went wrong. Color scheme was not updated.',
                {
                  variant: 'error'
                }
              );
            }
          }}
        >
          {({
            isSubmitting,
            values,
            setFieldValue,
            initialValues,
            errors,
            touched,
            handleSubmit,
            dirty,
            isValid,
            handleBlur
          }) => {
            // This is also what Formik does in their official documentation.
            // https://stackoverflow.com/questions/61031464/setstate-called-in-render-prop-is-causing-a-react-warning
            // Also see https://github.com/jaredpalmer/formik/issues/1218
            if (dirty) {
              setTimeout(() => setDirtyForm(true), 0);
            } else {
              setTimeout(() => setDirtyForm(false), 0);
            }

            return (
              <>
                {isSubmitting && (
                  <Loader type="fullscreen" position="centered">
                    Saving...
                  </Loader>
                )}
                <Prompt
                  when={!deepEqual(initialValues, values)}
                  message="You have unsaved changes, are you sure you want to exit?"
                />
                <Form onSubmit={handleSubmit}>
                  <Box mt={1}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Grid container alignItems="center" spacing={1}>
                          <Grid item xs={12} sm={4}>
                            <SMBSelect
                              required
                              name="schemeArea"
                              label="Color Scheme Areas"
                              id="schemeArea"
                              variant="outlined"
                              disabled={isLoadingGeneralInfo}
                              value={values.schemeArea}
                              onChange={({ target: { value } }) => {
                                setFieldValue('schemeArea', value);
                              }}
                            >
                              {(values.colorSchemeAreas || colorSchemeAreasAdd) &&
                                (values?.colorSchemeAreas
                                  ? values?.colorSchemeAreas
                                  : colorSchemeAreasAdd
                                ).map((scheme, index) => {
                                  return (
                                    <MenuItem key={`${index}`} value={scheme.value}>
                                      {scheme.description}
                                    </MenuItem>
                                  );
                                })}
                            </SMBSelect>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <SMBSelect
                              required
                              onChange={({ target: { value } }) => {
                                setFieldValue('isActive', value);
                              }}
                              variant="outlined"
                              name="isActive"
                              label="Is Color Scheme Active"
                              id="isActive"
                              value={values.isActive}
                            >
                              {[
                                { value: 'Yes', shorthand: 'Yes', description: 'Yes' },
                                { value: 'No', shorthand: 'No', description: 'No' }
                              ].map((active, index) => {
                                return (
                                  <MenuItem key={`${active.value}`} value={active.value}>
                                    {active.description}
                                  </MenuItem>
                                );
                              })}
                            </SMBSelect>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              type="number"
                              variant="outlined"
                              autoComplete="nope"
                              label="Upgrade Fee"
                              name="upgradeFee"
                              size="small"
                              value={values.upgradeFee}
                              onBlur={handleBlur}
                              onChange={e => setFieldValue('upgradeFee', Number(e.target.value))}
                              error={touched.upgradeFee && !!errors.upgradeFee}
                              helperText={touched.upgradeFee && errors.upgradeFee}
                            />
                          </Grid>
                          {values.schemeArea !== 'AllSidingSchemes' &&
                            values.schemeArea !== 'SidingUniversal' && (
                              <Grid item xs={12} sm={6} className={classes.colorOptions}>
                                <SMBSelect
                                  required
                                  name="brickMortarColor"
                                  label="Brick Mortar Combo"
                                  id="brickMortarColor"
                                  variant="outlined"
                                  disabled={isLoadingGeneralInfo}
                                  value={values?.brickMortarColor}
                                  onChange={async ({ target: { value } }) => {
                                    try {
                                      const res = await fetchColorSchemeOptionUris(
                                        'brickMortarColor',
                                        value
                                      );
                                      setFieldValue('brickMortarColorUri', res);
                                      setFieldValue('brickMortarColor', value);
                                    } catch (error) {
                                      console.error(
                                        'Failed to fetch color scheme option URIs:',
                                        error
                                      );
                                    }
                                  }}
                                >
                                  {(generalInfo?.brickMortarColor?.values ||
                                    brickMortarOptionsAdd) &&
                                    (generalInfo?.brickMortarColor?.values
                                      ? generalInfo?.brickMortarColor?.values
                                      : brickMortarOptionsAdd
                                    ).map((brick, index) => {
                                      return (
                                        <MenuItem key={`${index}`} value={brick.value}>
                                          {brick.description}
                                        </MenuItem>
                                      );
                                    })}
                                </SMBSelect>
                                <ColorOptions
                                  options={values?.brickMortarColorUri}
                                  name={'brickMortarColor'}
                                />
                              </Grid>
                            )}
                          <Grid item xs={12} sm={6}>
                            <SMBSelect
                              required
                              name="roofColor"
                              label="Roof Color"
                              id="roofColor"
                              variant="outlined"
                              disabled={isLoadingGeneralInfo}
                              value={values?.roofColor}
                              onChange={async ({ target: { value } }) => {
                                try {
                                  const res = await fetchColorSchemeOptionUris('roofColor', value);
                                  setFieldValue('roofColorUri', res);
                                  setFieldValue('roofColor', value);
                                } catch (error) {
                                  console.error('Failed to fetch color scheme option URIs:', error);
                                }
                              }}
                            >
                              {(generalInfo?.roofColor?.values || roofOptionsAdd) &&
                                (generalInfo?.roofColor?.values
                                  ? generalInfo?.roofColor?.values
                                  : roofOptionsAdd
                                ).map((roof, index) => {
                                  return (
                                    <MenuItem key={`${index}`} value={roof.value}>
                                      {roof.description}
                                    </MenuItem>
                                  );
                                })}
                            </SMBSelect>
                            {!isLoadingGeneralInfo && (
                              <ColorOptions options={values?.roofColorUri} name={'roofColor'} />
                            )}
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <SMBSelect
                              required
                              name="trimColor"
                              label="Soffit and Exterior Trim Color"
                              id="trimColor"
                              variant="outlined"
                              disabled={isLoadingGeneralInfo}
                              value={values?.trimColor}
                              onChange={async ({ target: { value } }) => {
                                try {
                                  const res = await fetchColorSchemeOptionUris('trimColor', value);
                                  setFieldValue('trimColorUri', res);
                                  setFieldValue('trimColor', value);
                                } catch (error) {
                                  console.error('Failed to fetch color scheme option URIs:', error);
                                }
                              }}
                            >
                              {(generalInfo?.trimColor?.values || trimOptionsAdd) &&
                                (generalInfo?.trimColor?.values
                                  ? generalInfo?.trimColor?.values
                                  : trimOptionsAdd
                                ).map((trim, index) => {
                                  return (
                                    <MenuItem key={`${index}`} value={trim.value}>
                                      {trim.description}
                                    </MenuItem>
                                  );
                                })}
                            </SMBSelect>
                            {!isLoadingGeneralInfo && (
                              <ColorOptions options={values?.trimColorUri} name={'trimColor'} />
                            )}
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <SMBSelect
                              required
                              name="windowColor"
                              label="Window Color"
                              id="windowColor"
                              variant="outlined"
                              disabled={isLoadingGeneralInfo}
                              value={values?.windowColor}
                              onChange={async ({ target: { value } }) => {
                                try {
                                  const res = await fetchColorSchemeOptionUris(
                                    'windowColor',
                                    value
                                  );
                                  setFieldValue('windowColorUri', res);
                                  setFieldValue('windowColor', value);
                                } catch (error) {
                                  console.error('Failed to fetch color scheme option URIs:', error);
                                }
                              }}
                            >
                              {(generalInfo?.windowColor?.values || windowOptionsAdd) &&
                                (generalInfo?.windowColor?.values
                                  ? generalInfo?.windowColor?.values
                                  : windowOptionsAdd
                                ).map((window, index) => {
                                  return (
                                    <MenuItem key={`${index}`} value={window.value}>
                                      {window.description}
                                    </MenuItem>
                                  );
                                })}
                            </SMBSelect>
                            {!isLoadingGeneralInfo && (
                              <ColorOptions options={values?.windowColorUri} name={'windowColor'} />
                            )}
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <SMBSelect
                              required
                              name="paintColor"
                              label="Siding Paint Swatch"
                              id="paintColor"
                              variant="outlined"
                              disabled={isLoadingGeneralInfo}
                              value={values?.paintColor}
                              onChange={async ({ target: { value } }) => {
                                try {
                                  const res = await fetchColorSchemeOptionUris('paintColor', value);
                                  setFieldValue('paintColorUris', res);
                                  setFieldValue('paintColor', value);
                                } catch (error) {
                                  console.error('Failed to fetch color scheme option URIs:', error);
                                }
                              }}
                            >
                              {(generalInfo?.paintColor?.values || paintOptionsAdd) &&
                                (generalInfo?.paintColor?.values
                                  ? generalInfo?.paintColor?.values
                                  : paintOptionsAdd
                                ).map((paint, index) => {
                                  return (
                                    <MenuItem key={`${index}`} value={paint.value}>
                                      {paint.description}
                                    </MenuItem>
                                  );
                                })}
                            </SMBSelect>
                            {!isLoadingGeneralInfo && (
                              <ColorOptions options={values?.paintColorUris} name={'paintColor'} />
                            )}
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <SMBSelect
                              required
                              name="shutterPaintColor"
                              label="Shutter Paint swatch"
                              id="shutterPaintColor"
                              variant="outlined"
                              disabled={isLoadingGeneralInfo}
                              value={values?.shutterPaintColor}
                              onChange={async ({ target: { value } }) => {
                                try {
                                  const res = await fetchColorSchemeOptionUris(
                                    'shutterPaintColor',
                                    value
                                  );
                                  setFieldValue('shutterPaintColorUris', res);
                                  setFieldValue('shutterPaintColor', value);
                                } catch (error) {
                                  console.error('Failed to fetch color scheme option URIs:', error);
                                }
                              }}
                            >
                              {(generalInfo?.shutterPaintColor?.values || shutterPaintOptionsAdd) &&
                                (generalInfo?.shutterPaintColor?.values
                                  ? generalInfo?.shutterPaintColor?.values
                                  : shutterPaintOptionsAdd
                                ).map((shutter, index) => {
                                  return (
                                    <MenuItem key={`${index}`} value={shutter.value}>
                                      {shutter.description}
                                    </MenuItem>
                                  );
                                })}
                            </SMBSelect>
                            {!isLoadingGeneralInfo && (
                              <ColorOptions
                                options={values?.shutterPaintColorUris}
                                name={'shutterPaintColor'}
                              />
                            )}
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <SMBSelect
                              required
                              name="garageDoorColor"
                              label="Garage Door Color"
                              id="garageDoorColor"
                              variant="outlined"
                              disabled={isLoadingGeneralInfo}
                              value={values?.garageDoorColor}
                              onChange={async ({ target: { value } }) => {
                                try {
                                  const res = await fetchColorSchemeOptionUris(
                                    'garageDoorColor',
                                    value
                                  );
                                  setFieldValue('garageDoorColorUri', res);
                                  setFieldValue('garageDoorColor', value);
                                } catch (error) {
                                  console.error('Failed to fetch color scheme option URIs:', error);
                                }
                              }}
                            >
                              {(generalInfo?.garageDoorColor?.values || garageDoorOptionsAdd) &&
                                (generalInfo?.garageDoorColor?.values
                                  ? generalInfo?.garageDoorColor?.values
                                  : garageDoorOptionsAdd
                                ).map((garageDoor, index) => {
                                  return (
                                    <MenuItem key={`${index}`} value={garageDoor.value}>
                                      {garageDoor.description}
                                    </MenuItem>
                                  );
                                })}
                            </SMBSelect>
                            {!isLoadingGeneralInfo && (
                              <ColorOptions
                                options={values?.garageDoorColorUri}
                                name={'garageDoorColor'}
                              />
                            )}
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box mt={1} mb={0.5}>
                    <Divider />
                  </Box>
                  <ButtonGroup>
                    <Button
                      disabled={!dirty || isSubmitting || !isValid}
                      type="submit"
                      startIcon={colorSchemeId ? <Save /> : <Add />}
                      variant="contained"
                      color="primary"
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="inherit"
                      startIcon={<Close />}
                      onClick={() => {
                        history.push('/development/color-schemes');
                      }}
                    >
                      Cancel
                    </Button>
                  </ButtonGroup>
                </Form>
              </>
            );
          }}
        </Formik>
      )}
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  bold: {
    fontWeight: 600
  },
  picture: {
    userSelect: 'none',
    cursor: 'pointer',
    '&:hover::before': {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      color: theme.palette.common.white,
      fontSize: '0.85rem',
      position: 'absolute',
      textAlign: 'center',
      height: 50,
      width: 50,
      padding: 4
    }
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    '& :not(:last-child)': {
      marginRight: theme.spacing(0.33)
    }
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'end',
    '& :not(:last-child)': {
      marginBottom: theme.spacing(0.33)
    }
  },
  colorImage: {
    width: 50,
    height: 50
  },
  colorOptions: {
    paddingTop: theme.spacing(2)
  }
}));
